'use client';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, MapPin, X, Loader2, Globe } from 'lucide-react';
import { AerodromeInfo } from '@/lib/mockWeatherData';
import { searchLocalAerodromes, fetchOurAirportsSearch } from '@/lib/ourairports';

interface AerodromeSearchProps {
  selected: AerodromeInfo;
  onSelect: (a: AerodromeInfo) => void;
}

export default function AerodromeSearch({ selected, onSelect }: AerodromeSearchProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<AerodromeInfo[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchSource, setSearchSource] = useState<'local' | 'ourairports' | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const doSearch = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      setOpen(false);
      setSearchSource(null);
      return;
    }

    // 1. Search local PIAF/OurAirports database first (instant)
    const local = searchLocalAerodromes(q);

    if (local.length > 0) {
      setResults(local);
      setSearchSource('local');
      setOpen(true);
      return;
    }

    // 2. Fall back to OurAirports API route (worldwide)
    setSearching(true);
    setSearchSource('ourairports');
    try {
      const worldwide = await fetchOurAirportsSearch(q);
      if (worldwide.length > 0) {
        setResults(worldwide);
        setOpen(true);
      } else {
        setResults([]);
        setOpen(true); // show "not found" message
      }
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(query), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, doSearch]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative w-full max-w-md">
      <div
        className="flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-150"
        style={{
          backgroundColor: 'var(--input-bg)',
          border: `1px solid ${open ? 'var(--accent-cyan)' : 'var(--border)'}`,
          boxShadow: open ? '0 0 0 3px rgba(0,212,255,0.08)' : 'none',
        }}
      >
        {searching
          ? <Loader2 size={16} className="animate-spin flex-shrink-0" style={{ color: 'var(--accent-cyan)' }} />
          : <Search size={16} className="flex-shrink-0" style={{ color: '#9fb2c8' }} />
        }
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.length >= 2 && setOpen(true)}
          placeholder="Code OACI ou nom de terrain…"
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-[#9fb2c8]"
          style={{ color: '#ffffff', fontFamily: 'DM Sans, sans-serif' }}
        />
        {query && (
          <button onClick={() => { setQuery(''); setOpen(false); setResults([]); setSearchSource(null); }}>
            <X size={14} style={{ color: '#9fb2c8' }} />
          </button>
        )}
      </div>

      {/* Selected aerodrome pill */}
      <div className="flex items-center gap-2 mt-2">
        <MapPin size={12} style={{ color: 'var(--accent-cyan)' }} />
        <span className="font-mono-data text-xs font-semibold" style={{ color: 'var(--accent-cyan)' }}>
          {selected.icao}
        </span>
        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
          {selected.name} · {selected.elevation}m AMSL
        </span>
      </div>

      {/* Dropdown */}
      {open && results.length > 0 && (
        <div
          className="absolute top-full left-0 right-0 mt-1 rounded-xl overflow-hidden z-50"
          style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--border)', boxShadow: '0 8px 32px rgba(0,0,0,0.35)' }}
        >
          {/* Source badge */}
          {searchSource === 'ourairports' && (
            <div className="flex items-center gap-1.5 px-4 py-1.5" style={{ borderBottom: '1px solid var(--border)' }}>
              <Globe size={11} style={{ color: '#9fb2c8' }} />
              <span className="text-xs" style={{ color: '#9fb2c8' }}>OurAirports — base mondiale</span>
            </div>
          )}
          {results.map((aerodrome) => (
            <button
              key={`search-${aerodrome.icao}`}
              onClick={() => { onSelect(aerodrome); setQuery(''); setOpen(false); setSearchSource(null); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/5 transition-colors duration-100"
            >
              <span
                className="font-mono-data text-sm font-semibold px-2 py-0.5 rounded flex-shrink-0"
                style={{ backgroundColor: 'rgba(72,217,255,0.14)', color: 'var(--accent-cyan)' }}
              >
                {aerodrome.icao}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: '#ffffff' }}>{aerodrome.name}</p>
                <p className="text-xs" style={{ color: '#9fb2c8' }}>{aerodrome.country} · {aerodrome.elevation}m</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {open && query.length >= 2 && results.length === 0 && !searching && (
        <div
          className="absolute top-full left-0 right-0 mt-1 rounded-xl px-4 py-3"
          style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--border)' }}
        >
          <p className="text-sm" style={{ color: '#9fb2c8' }}>Aucun terrain trouvé pour &quot;{query}&quot;</p>
        </div>
      )}
    </div>
  );
}
